import { CognitoUser, CognitoUserPool, AuthenticationDetails } from 'amazon-cognito-identity-js'
import { signInWithRedirect } from 'aws-amplify/auth'
import { Auth } from "@/utils/amplify-compat";
import config from '@/config'
import { Buffer } from 'buffer'

// Ensure Buffer exists for jwt/base64 handling in browsers
if (!window.Buffer) window.Buffer = Buffer

class CognitoAuth {
    constructor() {
        this.userSession = null
        this.tokens = null
    }

    configure(cfg) {
        if (typeof cfg !== 'object' || Array.isArray(cfg)) {
            throw new Error('[CognitoAuth error] valid option object required')
        }

        // amazon-cognito-identity-js local storage user pool
        this.userPool = new CognitoUserPool({
            UserPoolId: cfg.userPoolId,
            ClientId: cfg.clientId
        })

        // Amplify.configure() is now called in main.js before the app starts.
        // Storing options for reference only.
        this.options = cfg
    }

    isAuthenticated(cb) {
        const cognitoUser = this.getCurrentUser()
        if (cognitoUser) {
            cognitoUser.getSession((err, session) => {
                if (err || !session || !session.isValid()) {
                    return cb(err || new Error('No valid session'), false)
                }
                return cb(null, true)
            })
        } else {
            cb(null, false)
        }
    }

    authenticate(username, pass, cb) {
        const authenticationDetails = new AuthenticationDetails({ Username: username, Password: pass })
        const cognitoUser = new CognitoUser({ Username: username, Pool: this.userPool })

        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: async (result) => {
                try {
                    await Auth.currentCredentials()
                } catch (e) {
                    console.warn('[CognitoAuth] Auth.currentCredentials() after onSuccess:', e)
                }
                cb(null, result)
            },
            onFailure: (err) => cb(err)
        })
    }

    getCurrentUser() {
        return this.userPool.getCurrentUser()
    }

    getUserAttributes(cb) {
        const cognitoUser = this.getCurrentUser()
        if (cognitoUser != null) {
            cognitoUser.getUserAttributes((err, attrs) => {
                if (err) return cb(err, false)
                return cb(attrs, true)
            })
        } else {
            cb(null, false)
        }
    }

    async getIdTokenString() {
        return new Promise((resolve, reject) => {
            this.getIdToken((err, tok) => err ? reject(err) : resolve(tok))
        })
    }

    async getIdTokenPayload() {
        const tok = await this.getIdTokenString()
        return this.decodePayload(tok) || {}
    }

    async getGroups() {
        const payload = await this.getIdTokenPayload()
        return payload['cognito:groups'] || []
    }

    async isInGroup(groupName) {
        const groups = await this.getGroups()
        return Array.isArray(groups) && groups.includes(groupName)
    }

    signOut() {
        this.getCurrentUser()?.signOut()
        localStorage.removeItem('tokens')
    }

    getIdToken(cb) {
        if (this.getCurrentUser() == null) return cb(null, null)
        this.getCurrentUser().getSession((err, session) => {
            if (err) return cb(err)
            if (session && session.isValid()) return cb(null, session.getIdToken().getJwtToken())
            cb(Error('Session is invalid'))
        })
    }

    async getAwsCredentials() {
        const creds = await Auth.currentCredentials()
        if (!creds || !creds.accessKeyId) {
            throw new Error('[CognitoAuth] Failed to obtain AWS credentials')
        }
        return creds
    }

    sha256 = async (str) => crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))

    generateNonce = async () => {
        const hash = await this.sha256(crypto.getRandomValues(new Uint32Array(4)).toString())
        const hashArray = Array.from(new Uint8Array(hash))
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    }

    base64URLEncode = (buf) =>
        btoa(String.fromCharCode.apply(null, new Uint8Array(buf)))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

    calculateClockDrift = (iatAccessToken, iatIdToken) => {
        const now = Math.floor(new Date() / 1000)
        const iat = Math.min(iatAccessToken, iatIdToken)
        return now - iat
    }

    redirectToSignin = () => signInWithRedirect()

    decodePayload = (jwtToken) => {
        if (!jwtToken) return {}
        const payload = jwtToken.split('.')[1]
        try {
            return JSON.parse(Buffer.from(payload, 'base64').toString('utf8'))
        } catch (err) {
            console.log(err)
            return {}
        }
    }
}

// --- Named export: singleton instance for router/anywhere ---
export const cognito = new CognitoAuth()
cognito.configure(config)

// --- Default export: Vue 3 plugin that exposes $cognitoAuth/injection ---
const plugin = {
    install(app) {
        app.config.globalProperties.$cognitoAuth = cognito
        app.provide('cognitoAuth', cognito)
    }
}
export default plugin
